/**
 * The one line of trial context under the plan-step title: which plan, and how long it has left.
 *
 * Extracted from `SubscriptionWizardDialog` so the rule can be tested on its own. The conditions
 * it encodes are account-state combinations (trial vs Free vs lapsed vs subscriber) that are
 * otherwise only reachable by mounting the whole wizard against live company data — which is why
 * #2157 shipped: the Free-plan case was stated in the comment but never in the code, and nothing
 * could have caught the difference.
 *
 * WHO GETS A LINE: only an account genuinely ON a trial.
 *  - Free plan — Free is a plan, not a trial. `isTrial` is NOT sufficient to exclude it (#2157):
 *    the flag stays true after `planFeature` moves to `free`, because the downgrade path and the
 *    subscription-deleted webhook both clear the subscription without clearing it. A Free account
 *    therefore fell through to the trial strings, and `getPlanDisplayName('free')` interpolated
 *    the word "Free" into them — "Your Free trial ends in 14 days" — counting down something that
 *    does not exist.
 *  - Lapsed paid — no trial to describe either, and `isTrial` is false there anyway.
 *  - A subscriber changing plans — not being told anything they do not already know.
 *
 * A LAPSED trial keeps `isTrial` true, which is why "has ended" is a real branch rather than an
 * assumption that any trial still has time on it.
 *
 * `Math.round` on a fractional day count, NOT ceil, to match `usePromotionalBanner`'s `daysLeft`
 * exactly. That banner is on the page behind this dialog counting the same trial down, and the two
 * rounding the same 6.4 days to 7 and 6 is the sort of disagreement a customer notices and nobody
 * can explain. Whether the trial is OVER is asked of the date itself rather than of the rounded
 * figure, since `Math.round(-0.2)` is 0 and would read as "ends today".
 *
 * Today and tomorrow get their own strings: "ends in 1 days" is the kind of thing that makes a
 * product feel unfinished. Four explicit keys rather than interpolating a unit, per the i18n rules.
 *
 * `trialEndDate` can be absent (an account marked trial with no date on `payment`), and then the
 * line states the plan without making a claim about time.
 */
import dayjs from 'dayjs';

type TrialSubtitleInput = {
  isTrial: boolean;
  /** #2157 — the Free plan keeps `isTrial` true; see the note above. */
  isOnFreePlan: boolean;
  currentPlanFeature: string | undefined;
  trialEndDate: string | number | Date | null | undefined;
  /** The plan's display name, e.g. `getPlanDisplayName(currentPlanFeature)`. */
  getPlanName: (planFeature: string) => string;
  t: (key: string, vars?: Record<string, unknown>) => string;
};

export function buildTrialSubtitle({
  isTrial,
  isOnFreePlan,
  currentPlanFeature,
  trialEndDate,
  getPlanName,
  t,
}: TrialSubtitleInput): string | null {
  if (!isTrial || isOnFreePlan) return null;
  const plan = getPlanName(currentPlanFeature ?? '');
  if (!plan) return null;
  if (!trialEndDate) return t('You are on the {{plan}} trial.', { plan });

  const endsAt = dayjs(trialEndDate);
  // A date the backend could not produce must not render "Invalid Date" into a sentence.
  if (!endsAt.isValid()) return t('You are on the {{plan}} trial.', { plan });
  if (endsAt.isBefore(dayjs())) return t('Your {{plan}} trial has ended.', { plan });

  const days = Math.round(endsAt.diff(dayjs(), 'day', true));
  if (days === 0) return t('Your {{plan}} trial ends today.', { plan });
  if (days === 1) return t('Your {{plan}} trial ends tomorrow.', { plan });
  return t('Your {{plan}} trial ends in {{days}} days.', { plan, days });
}
